/**
 * DuplicateToTargetDialog — apply the copied recipe to any campaign/flow,
 * creating a config if the target has none yet. Pick provider → campaign → flow.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { ClipboardCheck, ShieldCheck } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';
import { SearchableSelect } from '@/components/ui/form/input/SearchableSelect';
import { trackerText } from '@/features/optimizer-center/tracker-status/tracker-text';
import { useFlowOptimizerTrackers } from '@/features/trackers/flow-optimizer';
import { cn } from '@/lib/utils';

import { useDuplicateTarget } from '../useDuplicateTarget';
import {
  defaultPasteFields,
  type CopiedOptimizerConfig,
  type PasteFieldKey,
} from '../optimizer-clipboard.types';
import { PasteFieldsChecklist } from './PasteFieldsChecklist';
import { backdropClick } from '@/components/ui';

export interface DuplicateToTargetDialogProps {
  copied: CopiedOptimizerConfig;
  onClose: () => void;
}

export const DuplicateToTargetDialog: FC<DuplicateToTargetDialogProps> = ({ copied, onClose }) => {
  const t = useDuplicateTarget(copied, onClose);
  const providers = useFlowOptimizerTrackers();
  const [fields, setFields] = useState<Set<PasteFieldKey>>(() =>
    defaultPasteFields(copied.recipe),
  );
  const toggle = (key: PasteFieldKey) =>
    setFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const ready = t.canApply && fields.size > 0 && !t.isApplying;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={backdropClick(!t.isApplying ? onClose : undefined)}
      role="dialog"
      aria-modal="true"
      aria-label="Paste config to a campaign"
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-border px-5 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ClipboardCheck className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold leading-tight">Paste config to a campaign</h3>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              From “{copied.source.campaignName}” → pick where to apply it.
            </p>
          </div>
        </div>

        <div className="space-y-3 px-5 py-4">
          <div className="inline-flex rounded-lg border border-border p-0.5">
            {providers.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => t.setProvider(p)}
                className={cn(
                  'rounded-md px-3 py-1 text-sm capitalize transition-colors',
                  t.provider === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                )}
              >
                {trackerText(p)}
              </button>
            ))}
          </div>
          <SearchableSelect
            options={t.campaigns}
            value={t.campaignId}
            onChange={t.setCampaignId}
            placeholder={t.loadingCampaigns ? 'Loading campaigns…' : 'Select a campaign'}
            searchPlaceholder="Search campaigns…"
          />
          <SearchableSelect
            options={t.flows}
            value={t.flowId}
            onChange={t.setFlowId}
            disabled={!t.campaignId}
            placeholder={
              !t.campaignId
                ? 'Select a campaign first'
                : t.loadingFlows
                  ? 'Loading flows…'
                  : 'Select a flow'
            }
            searchPlaceholder="Search flows…"
          />
          <PasteFieldsChecklist
            selected={fields}
            onToggle={toggle}
            hasPackage={Boolean(copied.recipe.packageId)}
          />
        </div>

        <div className="mx-5 mb-4 flex items-start gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2.5 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
          <span>
            <span className="font-medium text-foreground">Safe:</span> a new config is created paused
            (auto-run off) with only the ticked fields. No weights are pushed live.
          </span>
        </div>

        <div className="flex justify-end gap-2 border-t border-border bg-muted/10 px-5 py-3">
          <Button variant="outline" onClick={onClose} disabled={t.isApplying}>
            Cancel
          </Button>
          <Button onClick={() => t.apply(fields)} disabled={!ready} aria-busy={t.isApplying} className="gap-2">
            {t.isApplying && <LoadingSpinner size="sm" />}
            Apply config
          </Button>
        </div>
      </div>
    </div>
  );
};
